import { paintedCostume, paintedBitmapCostume } from './sb3.js';
import { ICONS } from './icons.js';
import { rasterizeSvg, svgIntrinsicSize, svgInnerMarkup } from './svg-quality.js';
import {
  boundsOfSegs,
  drawPath,
  ensurePath,
  hitAnchor,
  hitHandle as hitSegHandle,
  insertAt,
  isSmooth,
  makeCorner,
  makeSmooth,
  mapSegs,
  nearestOnPath,
  parsePathD,
  pathContains,
  pathToSvgD,
  ptsToSegs,
  setHandle,
  translateSegs,
} from './vector.js';

const TOOLS = [
  ['select', 'Select'],
  ['reshape', 'Reshape'],
  ['brush', 'Brush'],
  ['line', 'Line'],
  ['rect', 'Rectangle'],
  ['ellipse', 'Circle'],
  ['text', 'Text'],
  ['fill', 'Fill'],
  ['eraser', 'Eraser'],
  ['eyedrop', 'Eyedrop'],
  ['center', 'Center'],
];

const ACTIONS = [
  ['undo', 'Undo', 'undo'],
  ['redo', 'Redo', 'redo'],
  ['flip-h', 'Flip H', 'flipH'],
  ['flip-v', 'Flip V', 'flipV'],
  ['rotate', 'Rotate', 'rotate'],
  ['rotate-ccw', 'Rotate left', 'rotateCcw'],
  ['center-reset', 'Center', 'center'],
  ['group', 'Group', 'group'],
  ['ungroup', 'Ungroup', 'ungroup'],
  ['snap', 'Snap', 'snap'],
  ['clear', 'Clear', 'clear'],
];

const SNAP_DIST = 8;

export class PaintEditor {
  constructor(app) {
    this.app = app;
    this.canvas = document.getElementById('paint-canvas');
    this.svgLayer = document.getElementById('paint-svg-layer');
    this.artboard = document.getElementById('paint-artboard');
    this.ctx = this.canvas.getContext('2d');
    this.dpr = 1;
    this.syncHiDPI();
    this.tool = 'select';
    this.fill = '#ff8c1a';
    this.stroke = '#111111';
    this.width = 4;
    this.mode = 'svg';
    this.shapes = [];
    this.bitmap = null;
    this.undo = [];
    this.redo = [];
    this.drag = null;
    this.selected = -1;
    this.selection = [];
    this.activePoint = -1;
    this.center = { x: 240, y: 180 };
    this.zoom = 1;
    this.pan = null;
    this.spaceDown = false;
    this.snapCenters = true;
    try {
      if (localStorage.getItem('paint-snap-centers') === '0') this.snapCenters = false;
    } catch { /* ignore */ }
    this.buildTools();
    this.buildActions();
    this.buildZoom();
    this.bind();
    this.applyZoom();
    window.addEventListener('resize', () => this.applyZoom());
  }

  buildTools() {
    const el = document.getElementById('paint-tools');
    el.innerHTML = '';
    for (const [id, label] of TOOLS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tool-btn' + (id === this.tool ? ' active' : '');
      b.dataset.tip = label;
      b.setAttribute('aria-label', label);
      b.dataset.tool = id;
      b.innerHTML = ICONS[id] || label;
      b.onclick = () => {
        this.tool = id;
        el.querySelectorAll('button').forEach((x) => x.classList.toggle('active', x.dataset.tool === id));
        if (id === 'reshape' && this.selected >= 0) this.prepareReshape(this.shapes[this.selected]);
        this.redraw();
      };
      el.appendChild(b);
    }
  }

  buildActions() {
    const el = document.getElementById('paint-actions');
    if (!el) return;
    el.innerHTML = '';
    for (const [id, label, icon] of ACTIONS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'icon-btn paint-act';
      b.dataset.tip = label;
      b.setAttribute('aria-label', label);
      b.dataset.paint = id;
      b.innerHTML = ICONS[icon] || label;
      if (id === 'snap') {
        b.classList.toggle('active', this.snapCenters);
        b.onclick = () => {
          this.snapCenters = !this.snapCenters;
          try { localStorage.setItem('paint-snap-centers', this.snapCenters ? '1' : '0'); } catch { /* ignore */ }
          b.classList.toggle('active', this.snapCenters);
        };
      } else {
        b.onclick = () => this.action(id);
      }
      el.appendChild(b);
    }
  }

  buildZoom() {
    const el = document.getElementById('paint-zoom-bar');
    if (!el) return;
    el.innerHTML = '';
    const mk = (icon, title, fn) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'icon-btn';
      b.dataset.tip = title;
      b.setAttribute('aria-label', title);
      b.innerHTML = ICONS[icon];
      b.onclick = fn;
      return b;
    };
    el.append(
      mk('zoomOut', 'Zoom out', () => this.nudgeZoom(-1)),
      (() => {
        const b = document.createElement('button');
        b.type = 'button';
        b.id = 'paint-zoom-pct';
        b.className = 'paint-zoom-pct';
        b.dataset.tip = 'Reset';
        b.setAttribute('aria-label', 'Reset');
        b.onclick = () => this.setZoom(1);
        return b;
      })(),
      mk('zoomIn', 'Zoom in', () => this.nudgeZoom(1)),
      mk('zoomReset', 'Fit', () => this.fitZoom()),
    );
    this.updateZoomLabel();
  }

  bind() {
    document.getElementById('paint-fill').oninput = (e) => { this.fill = e.target.value; };
    document.getElementById('paint-stroke').oninput = (e) => { this.stroke = e.target.value; };
    document.getElementById('paint-width').oninput = (e) => { this.width = Number(e.target.value); };
    document.getElementById('costume-name').onchange = (e) => {
      const c = this.costume();
      if (c) c.name = e.target.value;
      this.app.renderCostumeList();
    };
    const vecBtn = document.getElementById('paint-mode-vector');
    const bmpBtn = document.getElementById('paint-mode-bitmap');
    if (vecBtn) vecBtn.onclick = () => {
      if (this.mode === 'svg') return;
      this.snapshot();
      this.toVector();
      this.commit();
      this.redraw();
    };
    if (bmpBtn) bmpBtn.onclick = () => {
      if (this.mode === 'bitmap') return;
      this.snapshot();
      this.toBitmap();
    };
    this.canvas.addEventListener('pointerdown', (e) => this.pointerDown(e));
    this.canvas.addEventListener('pointermove', (e) => {
      this.updateCursor(e);
      this.pointerMove(e);
    });
    this.onWinUp = () => {
      this.pan = null;
      this.pointerUp();
    };
    window.addEventListener('pointerup', this.onWinUp);
    window.addEventListener('pointercancel', this.onWinUp);
    const wrap = document.getElementById('paint-canvas-wrap');
    wrap.addEventListener('wheel', (e) => {
      if (this.app.tab !== 'costumes') return;
      e.preventDefault();
      const dir = e.deltaY > 0 ? -1 : 1;
      this.nudgeZoom(dir, e);
    }, { passive: false });
    wrap.addEventListener('pointerdown', (e) => {
      if (e.button === 1 || this.spaceDown) {
        e.preventDefault();
        this.pan = { x: e.clientX, y: e.clientY, sl: wrap.scrollLeft, st: wrap.scrollTop };
      }
    });
    window.addEventListener('pointermove', (e) => {
      if (!this.pan) return;
      wrap.scrollLeft = this.pan.sl - (e.clientX - this.pan.x);
      wrap.scrollTop = this.pan.st - (e.clientY - this.pan.y);
    });
    window.addEventListener('keydown', (e) => {
      if (this.app.tab !== 'costumes') return;
      if (e.code === 'Space' && !e.target.matches('input, textarea')) {
        this.spaceDown = true;
        this.canvas.style.cursor = 'grab';
        e.preventDefault();
      }
      if (e.target.matches('input, textarea')) return;
      if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        this.setSelection(this.shapes.map((_, i) => i));
        this.redraw();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'g' || e.key === 'G')) {
        e.preventDefault();
        this.action(e.shiftKey ? 'ungroup' : 'group');
        return;
      }
      if (this.mode === 'svg' && this.selection.length
          && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const step = e.shiftKey ? 1 : (e.ctrlKey || e.metaKey) ? 40 : 10;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        if (!this._nudging) {
          this.snapshot();
          this._nudging = true;
        }
        this.moveSelection(dx, dy);
        if (!e.shiftKey) this.maybeSnapSelection();
        this.commit();
        this.redraw();
        return;
      }
      if ((e.key === '+' || e.key === '=') && (e.ctrlKey || e.metaKey || !e.shiftKey)) {
        if (e.ctrlKey || e.metaKey || e.key === '+' || e.key === '=') {
          this.nudgeZoom(1);
          e.preventDefault();
        }
      }
      if ((e.key === '-' || e.key === '_') && !e.target.matches('input, textarea')) {
        this.nudgeZoom(-1);
        e.preventDefault();
      }
      if (e.key === '0' && (e.ctrlKey || e.metaKey)) {
        this.setZoom(1);
        e.preventDefault();
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && this.selection.length && this.mode === 'svg') {
        this.snapshot();
        const sh = this.shapes[this.selected];
        if (this.tool === 'reshape' && this.selection.length === 1 && sh?.segs && this.activePoint >= 0 && sh.segs.length > 2) {
          sh.segs.splice(this.activePoint, 1);
          this.activePoint = Math.min(this.activePoint, sh.segs.length - 1);
        } else {
          for (const i of [...this.selection].sort((a, b) => b - a)) this.shapes.splice(i, 1);
          this.setSelection([]);
          this.activePoint = -1;
        }
        this.commit();
        this.redraw();
      }
    });
    window.addEventListener('keyup', (e) => {
      if (e.code === 'Space') {
        this.spaceDown = false;
        if (!this.pan) this.canvas.style.cursor = '';
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        this._nudging = false;
      }
    });
  }

  zoomSteps() {
    return [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6];
  }

  nudgeZoom(dir, event) {
    const steps = this.zoomSteps();
    let i = 0;
    for (let s = 0; s < steps.length; s++) {
      if (this.zoom + 1e-6 >= steps[s]) i = s;
    }
    const next = steps[Math.max(0, Math.min(steps.length - 1, i + dir))];
    this.setZoom(next, event);
  }

  fitZoom() {
    const wrap = document.getElementById('paint-canvas-wrap');
    if (!wrap) return this.setZoom(1);
    const pad = 24;
    const sx = (wrap.clientWidth - pad) / 480;
    const sy = (wrap.clientHeight - pad) / 360;
    const z = Math.max(0.25, Math.min(6, Math.min(sx, sy)));
    this.setZoom(Math.round(z * 100) / 100);
  }

  setZoom(z, event) {
    const wrap = document.getElementById('paint-canvas-wrap');
    if (!wrap) {
      this.zoom = Math.max(0.25, Math.min(6, z));
      this.applyZoom();
      this.updateZoomLabel();
      this.redraw();
      return;
    }
    const wr = wrap.getBoundingClientRect();
    const cr = this.canvas.getBoundingClientRect();
    const viewX = event ? event.clientX - wr.left : wrap.clientWidth / 2;
    const viewY = event ? event.clientY - wr.top : wrap.clientHeight / 2;
    const costumeX = (wr.left + viewX - cr.left) * 480 / (cr.width || 480);
    const costumeY = (wr.top + viewY - cr.top) * 360 / (cr.height || 360);

    this.zoom = Math.max(0.25, Math.min(6, z));
    this.applyZoom();
    void wrap.offsetWidth;

    const wr2 = wrap.getBoundingClientRect();
    const cr2 = this.canvas.getBoundingClientRect();
    const nowX = cr2.left + costumeX * cr2.width / 480 - wr2.left;
    const nowY = cr2.top + costumeY * cr2.height / 360 - wr2.top;
    wrap.scrollLeft += nowX - viewX;
    wrap.scrollTop += nowY - viewY;
    this.updateZoomLabel();
    this.redraw();
  }

  applyZoom() {
    const wrap = document.getElementById('paint-canvas-wrap');
    const inner = document.getElementById('paint-canvas-inner');
    const board = this.artboard;
    const cw = 480 * this.zoom;
    const ch = 360 * this.zoom;
    if (board) {
      board.style.width = `${cw}px`;
      board.style.height = `${ch}px`;
    }
    this.canvas.style.width = `${cw}px`;
    this.canvas.style.height = `${ch}px`;
    if (this.svgLayer) {
      this.svgLayer.setAttribute('width', '480');
      this.svgLayer.setAttribute('height', '360');
      this.svgLayer.style.width = `${cw}px`;
      this.svgLayer.style.height = `${ch}px`;
    }
    if (inner && wrap) {
      const padX = Math.max(0, wrap.clientWidth - cw);
      const padY = Math.max(0, wrap.clientHeight - ch);
      inner.style.width = `${cw + padX}px`;
      inner.style.height = `${ch + padY}px`;
      const ml = `${padX / 2}px`;
      const mt = `${padY / 2}px`;
      if (board) {
        board.style.marginLeft = ml;
        board.style.marginTop = mt;
        this.canvas.style.marginLeft = '0';
        this.canvas.style.marginTop = '0';
      } else {
        this.canvas.style.marginLeft = ml;
        this.canvas.style.marginTop = mt;
      }
    }
  }

  updateZoomLabel() {
    const b = document.getElementById('paint-zoom-pct');
    if (b) b.textContent = `${Math.round(this.zoom * 100)}%`;
  }

  costume() {
    const t = this.app.current;
    if (!t) return null;
    return t.costumes[t.currentCostume];
  }

  loadCostume() {
    const c = this.costume();
    if (!c) return;
    document.getElementById('costume-name').value = c.name;
    this.undo = [];
    this.redo = [];
    this.setSelection([]);
    this.drag = null;
    if (c.dataFormat === 'svg') {
      this.mode = 'svg';
      this.bitmap = null;
      const rcx = c.rotationCenterX ?? 50;
      const rcy = c.rotationCenterY ?? 50;
      if (c._shapes?.length && c._shapes.some((s) => s.type && s.type !== 'image')) {
        this.shapes = cloneShapes(c._shapes);
        this.center = { x: 240, y: 180 };
      } else if (c._svg) {
        this.shapes = loadSvgShapes(c._svg, rcx, rcy);
        this.center = { x: 240, y: 180 };
      } else {
        this.shapes = [];
        this.center = { x: 240, y: 180 };
      }
      for (const sh of this.shapes) {
        if (sh.type === 'path' && !sh.segs?.length && sh.pts?.length) sh.segs = ptsToSegs(sh.pts);
      }
    } else {
      this.mode = 'bitmap';
      this.shapes = [];
      this.center = { x: 240, y: 180 };
      this.loadBitmap(c);
    }
    this.updateModeUI();
    this.redraw();
  }

  updateModeUI() {
    const vec = document.getElementById('paint-mode-vector');
    const bmp = document.getElementById('paint-mode-bitmap');
    if (vec) vec.classList.toggle('active', this.mode === 'svg');
    if (bmp) bmp.classList.toggle('active', this.mode === 'bitmap');
  }

  loadBitmap(c) {
    const img = new Image();
    img.onload = () => {
      this.bitmap = document.createElement('canvas');
      this.bitmap.width = 480;
      this.bitmap.height = 360;
      const g = this.bitmap.getContext('2d');
      const iw = img.naturalWidth || img.width;
      const ih = img.naturalHeight || img.height;
      const s = Math.min(480 / iw, 360 / ih, 1);
      const dw = iw * s;
      const dh = ih * s;
      const ox = (480 - dw) / 2;
      const oy = (360 - dh) / 2;
      g.drawImage(img, ox, oy, dw, dh);
      this.center = {
        x: ox + (c.rotationCenterX ?? iw / 2) * s,
        y: oy + (c.rotationCenterY ?? ih / 2) * s,
      };
      this.redraw();
    };
    if (c._data) {
      const type = c.dataFormat === 'svg' ? 'image/svg+xml' : `image/${c.dataFormat === 'jpg' ? 'jpeg' : c.dataFormat}`;
      img.src = URL.createObjectURL(new Blob([c._data], { type }));
    } else {
      this.bitmap = document.createElement('canvas');
      this.bitmap.width = 480;
      this.bitmap.height = 360;
    }
  }

  snapshot() {
    this.undo.push(JSON.stringify({
      mode: this.mode,
      shapes: cloneShapes(this.shapes),
      bmp: this.bitmap ? this.bitmap.toDataURL() : null,
      center: this.center,
    }));
    if (this.undo.length > 40) this.undo.shift();
    this.redo = [];
  }

  restore(pack) {
    const s = JSON.parse(pack);
    this.mode = s.mode;
    this.shapes = s.shapes || [];
    this.center = s.center || { x: 240, y: 180 };
    if (s.bmp) {
      const img = new Image();
      img.onload = () => {
        this.bitmap = document.createElement('canvas');
        this.bitmap.width = 480;
        this.bitmap.height = 360;
        this.bitmap.getContext('2d').drawImage(img, 0, 0);
        this.redraw();
      };
      img.src = s.bmp;
    } else this.bitmap = null;
    this.redraw();
  }

  ui(n) {
    return n / (this.zoom || 1);
  }

  uiLine(n = 1.5) {
    return n / (this.zoom || 1);
  }

  syncHiDPI() {
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    this.dpr = dpr;
    const scale = Math.min(dpr * (this.zoom || 1), 8);
    this.pxScale = scale;
    const w = Math.round(480 * scale);
    const h = Math.round(360 * scale);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.ctx.setTransform(scale, 0, 0, scale, 0, 0);
  }

  pt(e) {
    const r = this.canvas.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * 480 / r.width,
      y: (e.clientY - r.top) * 360 / r.height,
    };
  }

  pointerDown(e) {
    if (e.button === 1 || this.spaceDown) {
      const wrap = document.getElementById('paint-canvas-wrap');
      this.pan = { x: e.clientX, y: e.clientY, sl: wrap.scrollLeft, st: wrap.scrollTop };
      e.preventDefault();
      return;
    }
    const p = this.pt(e);
    this.canvas.setPointerCapture?.(e.pointerId);
    if (this.tool === 'center') {
      this.snapshot();
      const targets = [];
      const sel = this.selectedShapes();
      if (sel.length) {
        const b = unionBounds(sel);
        if (b) targets.push({ x: b.x + b.w / 2, y: b.y + b.h / 2 });
      }
      this.center = this.maybeSnapPoint(p, targets);
      this.commit();
      this.redraw();
      return;
    }
    if (this.tool === 'eyedrop') {
      const scale = this.pxScale || this.dpr;
      const pix = this.ctx.getImageData(Math.floor(p.x * scale), Math.floor(p.y * scale), 1, 1).data;
      const hex = '#' + [pix[0], pix[1], pix[2]].map((x) => x.toString(16).padStart(2, '0')).join('');
      this.fill = hex;
      document.getElementById('paint-fill').value = hex;
      return;
    }
    if (this.mode === 'bitmap') {
      this.snapshot();
      this.drag = { tool: this.tool, x0: p.x, y0: p.y, pts: [p] };
      if (this.tool === 'fill') this.flood(p.x, p.y, this.fill);
      if (this.tool === 'text') {
        const txt = prompt('Text', 'Hello');
        if (txt) {
          const g = this.bitmap.getContext('2d');
          g.fillStyle = this.fill;
          g.font = '28px sans-serif';
          g.textBaseline = 'alphabetic';
          g.fillText(txt, p.x, p.y);
        }
      }
      this.redraw();
      return;
    }

    if (this.tool === 'reshape') {
      this.onReshapeDown(p, e);
      return;
    }

    if (this.tool === 'select') {
      const i = this.hit(p);
      if (i >= 0) {
        if (e.shiftKey) this.toggleSelect(i);
        else if (!this.selection.includes(i)) this.setSelection([i]);
        this.snapshot();
        this.drag = { tool: 'select', x0: p.x, y0: p.y };
      } else {
        if (!e.shiftKey) this.setSelection([]);
        this.drag = { tool: 'marquee', x0: p.x, y0: p.y, shift: e.shiftKey };
      }
      this.redraw();
      return;
    }
    if (this.tool === 'fill') {
      const i = this.hit(p);
      if (i >= 0) {
        this.snapshot();
        this.fillShape(this.shapes[i], this.fill);
        this.commit();
      }
      this.redraw();
      return;
    }
    if (this.tool === 'text') {
      const txt = prompt('Text', 'Hello');
      if (txt) {
        this.snapshot();
        this.shapes.push({ type: 'text', x: p.x, y: p.y, text: txt, fill: this.fill, size: 24 });
        this.setSelection([this.shapes.length - 1]);
        this.commit();
      }
      this.redraw();
      return;
    }
    if (this.tool === 'eraser') {
      const i = this.hit(p);
      if (i >= 0) {
        this.snapshot();
        this.shapes.splice(i, 1);
        this.setSelection([]);
        this.commit();
      }
      this.redraw();
      return;
    }
    this.snapshot();
    this.drag = { tool: this.tool, x0: p.x, y0: p.y, pts: [p] };
    if (this.tool === 'brush') {
      this.shapes.push({ type: 'path', segs: ptsToSegs([{ ...p }]), stroke: this.stroke, width: this.width, fill: 'none' });
      this.setSelection([this.shapes.length - 1]);
    }
  }

  pointerMove(e) {
    if (this.pan) return;
    if (!this.drag) return;
    const p = this.pt(e);
    if (this.mode === 'bitmap') {
      this.drag.pts.push(p);
      this.drag.x1 = p.x;
      this.drag.y1 = p.y;
      const g = this.bitmap.getContext('2d');
      if (this.drag.tool === 'brush' || this.drag.tool === 'eraser') {
        g.strokeStyle = this.drag.tool === 'eraser' ? '#ffffff' : this.stroke;
        g.lineWidth = this.width;
        g.lineCap = 'round';
        const a = this.drag.pts[this.drag.pts.length - 2];
        g.beginPath();
        g.moveTo(a.x, a.y);
        g.lineTo(p.x, p.y);
        g.stroke();
      }
      this.redraw();
      if (['line', 'rect', 'ellipse'].includes(this.drag.tool)) this.previewShape(this.drag);
      return;
    }
    if (this.drag.tool === 'select' && this.selection.length) {
      this.moveSelection(p.x - this.drag.x0, p.y - this.drag.y0);
      this.drag.x0 = p.x;
      this.drag.y0 = p.y;
      this.maybeSnapSelection();
      this.redraw();
      return;
    }
    if (this.drag.tool === 'marquee') {
      this.drag.x1 = p.x;
      this.drag.y1 = p.y;
      this.redraw();
      this.drawMarquee(this.drag);
      return;
    }
    if (this.drag.tool === 'reshape') {
      this.drag.alt = e.altKey;
      this.applyHandle(this.drag, p);
      this.redraw();
      return;
    }
    if (this.drag.tool === 'brush' && this.selected >= 0) {
      const sh = this.shapes[this.selected];
      if (!sh.segs) sh.segs = [];
      sh.segs.push({ x: p.x, y: p.y, ix: 0, iy: 0, ox: 0, oy: 0, smooth: false });
      this.redraw();
      return;
    }
    this.drag.x1 = p.x;
    this.drag.y1 = p.y;
    this.redraw();
    this.previewShape(this.drag);
  }

  pointerUp() {
    if (!this.drag) return;
    if (this.mode === 'bitmap') {
      if (['line', 'rect', 'ellipse'].includes(this.drag.tool) && this.drag.x1 != null) {
        const g = this.bitmap.getContext('2d');
        g.strokeStyle = this.stroke;
        g.fillStyle = this.fill;
        g.lineWidth = this.width;
        this.drawPrim(g, this.drag);
      }
      this.drag = null;
      this.commit();
      this.redraw();
      return;
    }
    if (this.drag.tool === 'marquee') {
      this.applyMarquee(this.drag);
      this.drag = null;
      this.redraw();
      return;
    }
    if (['line', 'rect', 'ellipse'].includes(this.drag.tool) && this.drag.x1 != null) {
      const s = shapeFromDrag(this.drag, this.fill, this.stroke, this.width);
      if (Math.abs((this.drag.x1 ?? this.drag.x0) - this.drag.x0) > 1 || Math.abs((this.drag.y1 ?? this.drag.y0) - this.drag.y0) > 1) {
        this.shapes.push(s);
        this.setSelection([this.shapes.length - 1]);
      }
    }
    this.drag = null;
    this.commit();
    this.redraw();
  }

  setSelection(idxs) {
    const next = [];
    for (const i of idxs) {
      if (i >= 0 && i < this.shapes.length && !next.includes(i)) next.push(i);
    }
    this.selection = next;
    this.selected = next.length ? next[next.length - 1] : -1;
  }

  toggleSelect(i) {
    if (i < 0) return;
    if (this.selection.includes(i)) this.setSelection(this.selection.filter((x) => x !== i));
    else this.setSelection([...this.selection, i]);
  }

  selectedShapes() {
    return this.selection.map((i) => this.shapes[i]).filter(Boolean);
  }

  moveShape(s, dx, dy) {
    if (!s) return;
    if (s.type === 'group') {
      for (const c of s.children || []) this.moveShape(c, dx, dy);
      return;
    }
    if (s.segs) translateSegs(s.segs, dx, dy);
    else if (s.pts) s.pts.forEach((q) => { q.x += dx; q.y += dy; });
    else {
      s.x = (s.x ?? 0) + dx;
      s.y = (s.y ?? 0) + dy;
      if (s.x2 != null) s.x2 += dx;
      if (s.y2 != null) s.y2 += dy;
    }
  }

  moveSelection(dx, dy) {
    for (const s of this.selectedShapes()) this.moveShape(s, dx, dy);
  }

  maybeSnapShape(s) {
    if (!this.snapCenters || !s) return;
    const c = shapeCenter(s);
    const dx = this.center.x - c.x;
    const dy = this.center.y - c.y;
    const snap = this.ui(SNAP_DIST);
    if (dx * dx + dy * dy <= snap * snap) this.moveShape(s, dx, dy);
  }

  maybeSnapSelection() {
    if (!this.snapCenters || !this.selection.length) return;
    const b = unionBounds(this.selectedShapes());
    if (!b) return;
    const dx = this.center.x - (b.x + b.w / 2);
    const dy = this.center.y - (b.y + b.h / 2);
    const snap = this.ui(SNAP_DIST);
    if (dx * dx + dy * dy <= snap * snap) this.moveSelection(dx, dy);
  }

  fillShape(s, fill) {
    if (!s) return;
    if (s.type === 'group') {
      for (const c of s.children || []) this.fillShape(c, fill);
      return;
    }
    s.fill = fill;
  }

  applyMarquee(d) {
    const x = Math.min(d.x0, d.x1 ?? d.x0);
    const y = Math.min(d.y0, d.y1 ?? d.y0);
    const w = Math.abs((d.x1 ?? d.x0) - d.x0);
    const h = Math.abs((d.y1 ?? d.y0) - d.y0);
    if (w < 2 && h < 2) {
      if (!d.shift) this.setSelection([]);
      return;
    }
    const hit = [];
    this.shapes.forEach((s, i) => {
      const b = bounds(s);
      if (b.x < x + w && b.x + b.w > x && b.y < y + h && b.y + b.h > y) hit.push(i);
    });
    this.setSelection(d.shift ? [...this.selection, ...hit] : hit);
  }

  drawMarquee(d) {
    const g = this.ctx;
    const x = Math.min(d.x0, d.x1 ?? d.x0);
    const y = Math.min(d.y0, d.y1 ?? d.y0);
    const w = Math.abs((d.x1 ?? d.x0) - d.x0);
    const h = Math.abs((d.y1 ?? d.y0) - d.y0);
    g.save();
    g.strokeStyle = '#4d97ff';
    g.lineWidth = this.uiLine();
    g.setLineDash([this.ui(4), this.ui(3)]);
    g.strokeRect(x, y, w, h);
    g.restore();
  }

  groupSelection() {
    const idxs = [...this.selection].sort((a, b) => a - b);
    if (idxs.length < 2) return;
    const children = idxs.map((i) => this.shapes[i]);
    for (let i = idxs.length - 1; i >= 0; i--) this.shapes.splice(idxs[i], 1);
    const at = idxs[idxs.length - 1] - (idxs.length - 1);
    this.shapes.splice(at, 0, { type: 'group', children });
    this.setSelection([at]);
  }

  ungroupSelection() {
    const idxs = [...this.selection].sort((a, b) => a - b);
    let offset = 0;
    const next = [];
    for (const raw of idxs) {
      const i = raw + offset;
      const s = this.shapes[i];
      if (s?.type !== 'group' || !s.children?.length) {
        next.push(i);
        continue;
      }
      this.shapes.splice(i, 1, ...s.children);
      for (let k = 0; k < s.children.length; k++) next.push(i + k);
      offset += s.children.length - 1;
    }
    this.setSelection(next);
  }

  maybeSnapPoint(p, targets) {
    if (!this.snapCenters || !targets?.length) return p;
    for (const t of targets) {
      if (Math.hypot(p.x - t.x, p.y - t.y) <= this.ui(SNAP_DIST)) return { x: t.x, y: t.y };
    }
    return p;
  }

  prepareReshape(s) {
    if (!s || s.type === 'image' || s.type === 'text') return;
    ensurePath(s);
  }

  onReshapeDown(p, e) {
    const tol = this.ui(8);
    const now = performance.now();
    const dbl = now - (this._lastClickAt || 0) < 340
      && Math.hypot(p.x - (this._lastClickP?.x || 0), p.y - (this._lastClickP?.y || 0)) < tol;
    this._lastClickAt = now;
    this._lastClickP = { ...p };

    if (this.selected >= 0) this.prepareReshape(this.shapes[this.selected]);
    const cur = this.shapes[this.selected];

    if (cur?.segs) {
      const h = hitSegHandle(p, cur.segs, this.activePoint, tol);
      if (h) {
        this.snapshot();
        this.drag = { tool: 'reshape', kind: h.kind, index: this.selected, i: h.i, alt: e.altKey };
        e.preventDefault();
        this.redraw();
        return;
      }
      const ai = hitAnchor(p, cur.segs, tol);
      if (ai >= 0) {
        if (dbl) {
          this.snapshot();
          const sg = cur.segs[ai];
          if (isSmooth(sg) || sg.ix || sg.ox) makeCorner(sg);
          else makeSmooth(cur.segs, ai);
          this.activePoint = ai;
          this.commit();
          this.redraw();
          e.preventDefault();
          return;
        }
        this.snapshot();
        this.activePoint = ai;
        this.drag = { tool: 'reshape', kind: 'anchor', index: this.selected, i: ai };
        e.preventDefault();
        this.redraw();
        return;
      }
      const on = nearestOnPath(p, cur.segs, cur.closed);
      if (on && on.dist <= this.ui(8)) {
        this.snapshot();
        this.activePoint = insertAt(cur.segs, cur.closed, on);
        this.drag = { tool: 'reshape', kind: 'anchor', index: this.selected, i: this.activePoint };
        e.preventDefault();
        this.redraw();
        return;
      }
    }

    const i = this.hit(p);
    this.setSelection(i >= 0 ? [i] : []);
    this.activePoint = -1;
    if (i >= 0) {
      this.prepareReshape(this.shapes[i]);
      const sh = this.shapes[i];
      if (sh.segs) {
        const ai = hitAnchor(p, sh.segs, tol);
        if (ai >= 0) this.activePoint = ai;
      }
      this.snapshot();
      this.drag = { tool: 'reshape', kind: 'move', index: i, last: { ...p } };
    }
    this.redraw();
    e.preventDefault();
  }

  updateCursor(e) {
    if (this.tool === 'reshape') {
      const p = this.pt(e);
      const sh = this.shapes[this.selected];
      const tol = this.ui(8);
      if (sh?.segs && (hitSegHandle(p, sh.segs, this.activePoint, tol) || hitAnchor(p, sh.segs, tol) >= 0)) {
        this.canvas.style.cursor = 'grab';
      } else if (this.hit(p) >= 0) this.canvas.style.cursor = 'default';
      else this.canvas.style.cursor = 'default';
    } else if (this.tool === 'select') {
      this.canvas.style.cursor = 'default';
    } else {
      this.canvas.style.cursor = 'crosshair';
    }
  }

  applyHandle(drag, p) {
    const s = this.shapes[drag.index];
    if (!s) return;
    if (drag.kind === 'move') {
      const last = drag.last || p;
      this.moveShape(s, p.x - last.x, p.y - last.y);
      drag.last = { x: p.x, y: p.y };
      this.maybeSnapShape(s);
      return;
    }
    if (!s.segs) return;
    const sg = s.segs[drag.i];
    if (!sg) return;
    if (drag.kind === 'anchor') {
      sg.x = p.x;
      sg.y = p.y;
      this.activePoint = drag.i;
      return;
    }
    if (drag.kind === 'hin' || drag.kind === 'hout') {
      setHandle(sg, drag.kind, p.x, p.y, !drag.alt);
      this.activePoint = drag.i;
    }
  }

  hitHandle(p) {
    if (this.selected < 0) return null;
    const s = this.shapes[this.selected];
    if (!s) return null;
    const near = (x, y) => Math.hypot(p.x - x, p.y - y) <= this.ui(8);
    if (s.pts) {
      for (let i = 0; i < s.pts.length; i++) {
        if (near(s.pts[i].x, s.pts[i].y)) return { kind: 'pt', index: this.selected, i };
      }
    }
    if (s.type === 'ellipse') {
      for (const h of ellipseHandles(s)) {
        if (near(h.x, h.y)) return { kind: 'ellipse', index: this.selected, dir: h.dir };
      }
    }
    if (s.type === 'line') {
      if (near(s.x, s.y)) return { kind: 'end', index: this.selected, which: 1 };
      if (near(s.x2, s.y2)) return { kind: 'end', index: this.selected, which: 2 };
    }
    const b = bounds(s);
    const corners = [
      [b.x, b.y, 0, 0],
      [b.x + b.w, b.y, 1, 0],
      [b.x, b.y + b.h, 0, 1],
      [b.x + b.w, b.y + b.h, 1, 1],
    ];
    for (const [x, y, cx, cy] of corners) {
      if (near(x, y)) return { kind: 'corner', index: this.selected, cx, cy };
    }
    return null;
  }

  previewShape(drag) {
    const g = this.ctx;
    g.save();
    g.strokeStyle = this.stroke;
    g.fillStyle = this.fill;
    g.lineWidth = this.width;
    this.drawPrim(g, drag);
    g.restore();
  }

  drawPrim(g, d) {
    const x = Math.min(d.x0, d.x1 ?? d.x0);
    const y = Math.min(d.y0, d.y1 ?? d.y0);
    const w = Math.abs((d.x1 ?? d.x0) - d.x0);
    const h = Math.abs((d.y1 ?? d.y0) - d.y0);
    if (d.tool === 'line') {
      g.beginPath();
      g.moveTo(d.x0, d.y0);
      g.lineTo(d.x1, d.y1);
      g.stroke();
    } else if (d.tool === 'rect') {
      g.fillRect(x, y, w, h);
      g.strokeRect(x, y, w, h);
    } else if (d.tool === 'ellipse') {
      const dia = Math.max(w, h) || 1;
      g.beginPath();
      g.ellipse(x + w / 2, y + h / 2, dia / 2, dia / 2, 0, 0, Math.PI * 2);
      g.fill();
      g.stroke();
    }
  }

  hit(p) {
    for (let i = this.shapes.length - 1; i >= 0; i--) {
      if (hitShape(this.shapes[i], p, this.ui.bind(this))) return i;
    }
    return -1;
  }

  redraw() {
    this.syncHiDPI();
    this.syncSvgLayer();
    const g = this.ctx;
    g.clearRect(0, 0, 480, 360);
    if (this.mode === 'bitmap' && this.bitmap) {
      g.drawImage(this.bitmap, 0, 0);
    } else {
      if (!this.svgLayer) {
        for (const s of this.shapes) this.drawShape(g, s);
      }
      const list = this.selectedShapes();
      if (list.length === 1) this.drawSelection(g, list[0]);
      else if (list.length > 1) {
        const b = unionBounds(list);
        if (b) this.drawSelection(g, { type: 'rect', x: b.x, y: b.y, w: b.w, h: b.h });
      }
    }
    const lw = this.uiLine();
    this.drawCross(g, this.center.x, this.center.y, '#4d97ff', this.ui(8));
    g.strokeStyle = '#4d97ff';
    g.lineWidth = lw;
    g.beginPath();
    g.arc(this.center.x, this.center.y, this.ui(4), 0, Math.PI * 2);
    g.stroke();
  }

  drawCross(g, x, y, color, size) {
    g.save();
    g.strokeStyle = color;
    g.lineWidth = this.uiLine();
    g.beginPath();
    g.moveTo(x - size, y);
    g.lineTo(x + size, y);
    g.moveTo(x, y - size);
    g.lineTo(x, y + size);
    g.stroke();
    g.restore();
  }

  syncSvgLayer() {
    const layer = this.svgLayer;
    if (!layer) return;
    layer.replaceChildren();
    if (this.mode !== 'svg') return;
    const doc = new DOMParser().parseFromString(shapesToSvg(this.shapes), 'image/svg+xml');
    const root = doc.documentElement;
    if (!root || doc.querySelector('parsererror')) return;
    for (const child of [...root.childNodes]) {
      layer.appendChild(document.importNode(child, true));
    }
  }

  drawSelection(g, s) {
    const b = bounds(s);
    const lw = this.uiLine();
    const pad = this.ui(2);
    const dash = this.ui(4);
    const hs = this.ui(5);
    g.save();
    g.strokeStyle = '#4d97ff';
    g.lineWidth = lw;
    g.setLineDash([dash, dash * 0.75]);
    g.strokeRect(b.x - pad, b.y - pad, b.w + pad * 2, b.h + pad * 2);
    g.setLineDash([]);
    if (this.tool === 'reshape' && s.segs?.length) {
      const ap = s.segs[this.activePoint];
      if (ap && (ap.ix || ap.iy || ap.ox || ap.oy)) {
        g.strokeStyle = '#4d97ff';
        g.lineWidth = lw;
        if (ap.ix || ap.iy) {
          g.beginPath();
          g.moveTo(ap.x, ap.y);
          g.lineTo(ap.x + ap.ix, ap.y + ap.iy);
          g.stroke();
        }
        if (ap.ox || ap.oy) {
          g.beginPath();
          g.moveTo(ap.x, ap.y);
          g.lineTo(ap.x + ap.ox, ap.y + ap.oy);
          g.stroke();
        }
        g.fillStyle = '#fff';
        const circ = (x, y) => {
          g.beginPath();
          g.arc(x, y, hs + this.ui(1), 0, Math.PI * 2);
          g.fill();
          g.stroke();
        };
        if (ap.ix || ap.iy) circ(ap.x + ap.ix, ap.y + ap.iy);
        if (ap.ox || ap.oy) circ(ap.x + ap.ox, ap.y + ap.oy);
      }
      s.segs.forEach((sg, i) => {
        g.fillStyle = i === this.activePoint ? '#4d97ff' : '#fff';
        g.strokeStyle = '#4d97ff';
        g.lineWidth = lw;
        g.fillRect(sg.x - hs, sg.y - hs, hs * 2, hs * 2);
        g.strokeRect(sg.x - hs, sg.y - hs, hs * 2, hs * 2);
      });
    } else {
      g.fillStyle = '#fff';
      g.lineWidth = lw;
      [
        { x: b.x, y: b.y },
        { x: b.x + b.w, y: b.y },
        { x: b.x, y: b.y + b.h },
        { x: b.x + b.w, y: b.y + b.h },
      ].forEach((d) => {
        g.fillRect(d.x - hs, d.y - hs, hs * 2, hs * 2);
        g.strokeRect(d.x - hs, d.y - hs, hs * 2, hs * 2);
      });
      const cx = b.x + b.w / 2;
      const cy = b.y + b.h / 2;
      this.drawCross(g, cx, cy, '#ff8c1a', this.ui(7));
      g.beginPath();
      g.arc(cx, cy, this.ui(3.5), 0, Math.PI * 2);
      g.strokeStyle = '#ff8c1a';
      g.lineWidth = lw;
      g.stroke();
    }
    g.restore();
  }

  drawShape(g, s) {
    g.save();
    g.fillStyle = s.fill && s.fill !== 'none' ? s.fill : 'transparent';
    g.strokeStyle = s.stroke || '#111';
    g.lineWidth = s.width || 2;
    g.lineJoin = 'round';
    g.lineCap = 'round';
    if (s.type === 'path') {
      if (!s.segs?.length && s.pts?.length) s.segs = ptsToSegs(s.pts);
      if (!s.segs?.length) { g.restore(); return; }
      drawPath(g, s.segs, s.closed);
      if (s.fill && s.fill !== 'none') g.fill();
      if (s.stroke && s.stroke !== 'none') g.stroke();
    } else if (s.type === 'rect') {
      if (s.fill && s.fill !== 'none') g.fillRect(s.x, s.y, s.w, s.h);
      if (s.stroke && s.stroke !== 'none') g.strokeRect(s.x, s.y, s.w, s.h);
    } else if (s.type === 'ellipse') {
      g.beginPath();
      g.ellipse(s.x, s.y, Math.abs(s.w / 2) || 1, Math.abs(s.h / 2) || 1, 0, 0, Math.PI * 2);
      if (s.fill && s.fill !== 'none') g.fill();
      if (s.stroke && s.stroke !== 'none') g.stroke();
    } else if (s.type === 'line') {
      g.beginPath();
      g.moveTo(s.x, s.y);
      g.lineTo(s.x2, s.y2);
      g.stroke();
    } else if (s.type === 'text') {
      g.fillStyle = s.fill || '#111';
      g.font = `${s.size || 24}px sans-serif`;
      g.textBaseline = 'alphabetic';
      g.fillText(s.text || '', s.x, s.y);
    } else if (s.type === 'group') {
      for (const c of s.children || []) this.drawShape(g, c);
    } else if (s.type === 'image' && s.svg) {
      // Imported SVG is drawn as live vector on #paint-svg-layer.
    }
    g.restore();
  }

  flood(x, y, hex) {
    if (!this.bitmap) return;
    const g = this.bitmap.getContext('2d');
    const w = this.bitmap.width, h = this.bitmap.height;
    const img = g.getImageData(0, 0, w, h);
    const d = img.data;
    const i0 = (Math.floor(y) * w + Math.floor(x)) * 4;
    const tr = d[i0], tg = d[i0 + 1], tb = d[i0 + 2], ta = d[i0 + 3];
    const nr = parseInt(hex.slice(1, 3), 16), ng = parseInt(hex.slice(3, 5), 16), nb = parseInt(hex.slice(5, 7), 16);
    if (tr === nr && tg === ng && tb === nb) return;
    const stack = [[Math.floor(x), Math.floor(y)]];
    while (stack.length) {
      const [cx, cy] = stack.pop();
      if (cx < 0 || cy < 0 || cx >= w || cy >= h) continue;
      const i = (cy * w + cx) * 4;
      if (d[i] !== tr || d[i + 1] !== tg || d[i + 2] !== tb || d[i + 3] !== ta) continue;
      d[i] = nr; d[i + 1] = ng; d[i + 2] = nb; d[i + 3] = 255;
      stack.push([cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]);
    }
    g.putImageData(img, 0, 0);
  }

  action(name) {
    if (name === 'undo' && this.undo.length) {
      this.redo.push(JSON.stringify({ mode: this.mode, shapes: cloneShapes(this.shapes), bmp: this.bitmap ? this.bitmap.toDataURL() : null, center: this.center }));
      this.restore(this.undo.pop());
      this.commit();
      return;
    }
    if (name === 'redo' && this.redo.length) {
      this.undo.push(JSON.stringify({ mode: this.mode, shapes: cloneShapes(this.shapes), bmp: this.bitmap ? this.bitmap.toDataURL() : null, center: this.center }));
      this.restore(this.redo.pop());
      this.commit();
      return;
    }
    if (name === 'group' && this.selection.length < 2) return;
    if (name === 'ungroup' && !this.selectedShapes().some((s) => s.type === 'group')) return;
    this.snapshot();
    if (name === 'flip-h') this.transform(-1, 1);
    if (name === 'flip-v') this.transform(1, -1);
    if (name === 'rotate') this.rotateBy(1);
    if (name === 'rotate-ccw') this.rotateBy(-1);
    if (name === 'center-reset' || name === 'center') this.alignCenters();
    if (name === 'group') this.groupSelection();
    if (name === 'ungroup') this.ungroupSelection();
    if (name === 'clear') { this.shapes = []; this.setSelection([]); if (this.bitmap) this.bitmap.getContext('2d').clearRect(0, 0, 480, 360); }
    this.commit();
    this.redraw();
  }

  targetShapes() {
    const list = this.selectedShapes();
    if (this.mode === 'svg' && list.length) return list;
    return this.shapes;
  }

  targetPivot(list) {
    if (list.length) {
      const b = unionBounds(list);
      if (b) return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
    }
    return { x: 240, y: 180 };
  }

  transform(sx, sy) {
    if (this.mode === 'bitmap' && this.bitmap) {
      const tmp = document.createElement('canvas');
      tmp.width = 480; tmp.height = 360;
      const g = tmp.getContext('2d');
      g.translate(sx < 0 ? 480 : 0, sy < 0 ? 360 : 0);
      g.scale(sx, sy);
      g.drawImage(this.bitmap, 0, 0);
      this.bitmap = tmp;
      return;
    }
    const list = this.targetShapes();
    const p = this.targetPivot(list);
    const fn = (x, y) => ({
      x: p.x + (x - p.x) * sx,
      y: p.y + (y - p.y) * sy,
    });
    for (const s of list) mapShape(s, fn);
  }

  rotateBy(dir) {
    if (this.mode === 'bitmap' && this.bitmap) {
      const tmp = document.createElement('canvas');
      tmp.width = 480; tmp.height = 360;
      const g = tmp.getContext('2d');
      g.translate(240, 180);
      g.rotate(dir * Math.PI / 2);
      g.drawImage(this.bitmap, -240, -180);
      this.bitmap = tmp;
      return;
    }
    const list = this.targetShapes();
    const p = this.targetPivot(list);
    const fn = (x, y) => {
      const dx = x - p.x, dy = y - p.y;
      return dir > 0
        ? { x: p.x - dy, y: p.y + dx }
        : { x: p.x + dy, y: p.y - dx };
    };
    for (const s of list) mapShape(s, fn, true);
  }

  alignCenters() {
    if (this.mode !== 'svg' || !this.shapes.length) {
      this.center = { x: 240, y: 180 };
      return;
    }
    const list = this.selectedShapes().length ? this.selectedShapes() : this.shapes;
    const b = unionBounds(list);
    if (!b) {
      this.center = { x: 240, y: 180 };
      return;
    }
    translateShapes(list, this.center.x - (b.x + b.w / 2), this.center.y - (b.y + b.h / 2));
  }

  async toBitmap() {
    if (this.mode === 'bitmap') {
      this.commit();
      this.redraw();
      return;
    }
    const svg = shapesToSvg(this.shapes);
    const img = await rasterizeSvg(svg, 1);
    this.bitmap = document.createElement('canvas');
    this.bitmap.width = 480;
    this.bitmap.height = 360;
    const g = this.bitmap.getContext('2d');
    if (img?.canvas) {
      g.drawImage(img.canvas, img.x || 0, img.y || 0, img.width, img.height);
    }
    this.mode = 'bitmap';
    this.shapes = [];
    this.setSelection([]);
    this.syncSvgLayer();
    this.updateModeUI();
    this.commit();
    this.redraw();
  }

  toVector() {
    if (this.bitmap) {
      const url = this.bitmap.toDataURL('image/png');
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="480" height="360" viewBox="0 0 480 360"><image href="${url}" xlink:href="${url}" width="480" height="360"/></svg>`;
      this.shapes = [{ type: 'image', svg, x: 0, y: 0, w: 480, h: 360 }];
    }
    this.mode = 'svg';
    this.bitmap = null;
    this.updateModeUI();
  }

  async commit() {
    const t = this.app.current;
    if (!t) return;
    const old = t.costumes[t.currentCostume];
    const name = old?.name || (t.isStage ? 'backdrop1' : 'costume1');
    try {
      if (this.mode === 'svg') {
        const svg = shapesToSvg(this.shapes);
        const next = paintedCostume(name, svg);
        next.rotationCenterX = this.center.x;
        next.rotationCenterY = this.center.y;
        next._shapes = cloneShapes(this.shapes);
        t.costumes[t.currentCostume] = next;
        await this.app.player.cacheCostume(next);
      } else if (this.bitmap) {
        const blob = await new Promise((res) => this.bitmap.toBlob(res, 'image/png'));
        const buf = new Uint8Array(await blob.arrayBuffer());
        const next = paintedBitmapCostume(name, buf, 480, 360);
        next.rotationCenterX = this.center.x;
        next.rotationCenterY = this.center.y;
        t.costumes[t.currentCostume] = next;
        await this.app.player.cacheCostume(next);
      }
      this.app.renderCostumeList();
      this.app.renderSprites();
      this.app.player.draw();
    } catch (err) {
      console.error('Costume save failed', err);
    }
  }
}

function loadSvgShapes(svg, rcx, rcy) {
  const parsed = parseSvg(svg);
  if (!parsed.length) {
    const size = svgIntrinsicSize(svg);
    return [{
      type: 'image',
      svg,
      x: 240 - rcx,
      y: 180 - rcy,
      w: size.w,
      h: size.h,
    }];
  }
  const size = svgIntrinsicSize(svg);
  const artboard = Math.abs(size.vw - 480) < 0.5 && Math.abs(size.vh - 360) < 0.5
    && Math.abs(size.vx) < 0.5 && Math.abs(size.vy) < 0.5;
  if (!artboard || Math.abs(rcx - 240) > 0.5 || Math.abs(rcy - 180) > 0.5) {
    translateShapes(parsed, 240 - rcx, 180 - rcy);
  }
  return parsed;
}

function shapeCenter(s) {
  const b = bounds(s);
  return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
}

function mapShape(s, fn, swapSize) {
  if (!s) return;
  if (s.type === 'group') {
    for (const c of s.children || []) mapShape(c, fn, swapSize);
    return;
  }
  if (s.segs) mapSegs(s.segs, fn);
  else if (s.pts) s.pts.forEach((pt) => { const n = fn(pt.x, pt.y); pt.x = n.x; pt.y = n.y; });
  else if (s.type === 'line') {
    const a = fn(s.x, s.y);
    const b = fn(s.x2, s.y2);
    s.x = a.x; s.y = a.y; s.x2 = b.x; s.y2 = b.y;
  } else if (s.type === 'rect') {
    const a = fn(s.x, s.y);
    const b = fn(s.x + s.w, s.y + s.h);
    s.x = Math.min(a.x, b.x);
    s.y = Math.min(a.y, b.y);
    s.w = Math.abs(b.x - a.x);
    s.h = Math.abs(b.y - a.y);
  } else if (s.type === 'ellipse') {
    const c = fn(s.x, s.y);
    const r = fn(s.x + s.w / 2, s.y + s.h / 2);
    s.x = c.x;
    s.y = c.y;
    s.w = Math.abs(r.x - c.x) * 2;
    s.h = Math.abs(r.y - c.y) * 2;
  } else {
    const n = fn(s.x ?? 0, s.y ?? 0);
    s.x = n.x; s.y = n.y;
    if (s.x2 != null) { const q = fn(s.x2, s.y2); s.x2 = q.x; s.y2 = q.y; }
    if (swapSize && (s.type === 'image') && s.w != null && s.h != null) {
      const t = s.w; s.w = s.h; s.h = t;
    }
  }
}

function cloneShapes(shapes) {
  return JSON.parse(JSON.stringify(shapes || [], (k, v) => (String(k).startsWith('_') ? undefined : v)));
}

function unionBounds(shapes) {
  if (!shapes?.length) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of shapes) {
    const b = bounds(s);
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.w);
    maxY = Math.max(maxY, b.y + b.h);
  }
  if (!Number.isFinite(minX)) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function translateShapes(shapes, dx, dy) {
  for (const s of shapes) {
    if (s.type === 'group') translateShapes(s.children || [], dx, dy);
    else if (s.segs) translateSegs(s.segs, dx, dy);
    else if (s.pts) s.pts.forEach((p) => { p.x += dx; p.y += dy; });
    else {
      if (s.x != null) s.x += dx;
      if (s.y != null) s.y += dy;
      if (s.x2 != null) s.x2 += dx;
      if (s.y2 != null) s.y2 += dy;
    }
  }
}

function bounds(s) {
  if (s.type === 'group') return unionBounds(s.children || []) || { x: 0, y: 0, w: 1, h: 1 };
  if (s.segs?.length) return boundsOfSegs(s.segs);
  if (s.type === 'rect') return { x: s.x, y: s.y, w: s.w, h: s.h };
  if (s.type === 'ellipse') return { x: s.x - s.w / 2, y: s.y - s.h / 2, w: s.w, h: s.h };
  if (s.type === 'line') {
    return {
      x: Math.min(s.x, s.x2), y: Math.min(s.y, s.y2),
      w: Math.abs(s.x2 - s.x) || 1, h: Math.abs(s.y2 - s.y) || 1,
    };
  }
  if (s.type === 'text') return { x: s.x, y: s.y - (s.size || 24), w: Math.max(40, (s.text || '').length * (s.size || 24) * 0.55), h: s.size || 24 };
  if (s.type === 'image') {
    const size = s.svg ? svgIntrinsicSize(s.svg) : null;
    return {
      x: s.x || 0,
      y: s.y || 0,
      w: s.w || size?.w || 100,
      h: s.h || size?.h || 100,
    };
  }
  if (s.pts?.length) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of s.pts) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }
    return { x: minX, y: minY, w: maxX - minX || 1, h: maxY - minY || 1 };
  }
  return { x: s.x || 0, y: s.y || 0, w: 10, h: 10 };
}

function setBounds(s, b) {
  if (s.type === 'rect' || s.type === 'image' || s.type === 'text') {
    s.x = b.x; s.y = s.type === 'text' ? b.y + (s.size || 24) : b.y; s.w = b.w; s.h = b.h;
  } else if (s.type === 'ellipse') {
    s.x = b.x + b.w / 2; s.y = b.y + b.h / 2; s.w = b.w; s.h = b.h;
  } else if (s.pts?.length) {
    const o = bounds(s);
    const sx = b.w / (o.w || 1), sy = b.h / (o.h || 1);
    for (const p of s.pts) {
      p.x = b.x + (p.x - o.x) * sx;
      p.y = b.y + (p.y - o.y) * sy;
    }
  }
}

function ellipseHandles(s) {
  const rx = Math.abs(s.w / 2), ry = Math.abs(s.h / 2);
  return [
    { x: s.x, y: s.y - ry, dir: 'n' },
    { x: s.x + rx, y: s.y, dir: 'e' },
    { x: s.x, y: s.y + ry, dir: 's' },
    { x: s.x - rx, y: s.y, dir: 'w' },
  ];
}

function shapeFromDrag(d, fill, stroke, width) {
  if (d.tool === 'line') return { type: 'line', x: d.x0, y: d.y0, x2: d.x1, y2: d.y1, stroke, width, fill: 'none' };
  const x = Math.min(d.x0, d.x1), y = Math.min(d.y0, d.y1);
  const w = Math.abs(d.x1 - d.x0), h = Math.abs(d.y1 - d.y0);
  if (d.tool === 'rect') return { type: 'rect', x, y, w, h, fill, stroke, width };
  const dia = Math.max(w, h, 2);
  return { type: 'ellipse', x: x + w / 2, y: y + h / 2, w: dia, h: dia, fill, stroke, width, circle: true };
}

function shapeToSvg(s) {
  if (s.type === 'group') {
    return `<g data-editor-group="1">${(s.children || []).map(shapeToSvg).join('')}</g>`;
  }
    if (s.type === 'path') {
      if (!s.segs?.length && s.pts?.length) s.segs = ptsToSegs(s.pts);
      const d = pathToSvgD(s.segs || [], s.closed);
      const fill = s.fill && s.fill !== 'none' ? s.fill : 'none';
      return `<path d="${d}" fill="${fill}" stroke="${esc(s.stroke && s.stroke !== 'none' ? s.stroke : 'none')}" stroke-width="${s.width || 2}" stroke-linecap="round" stroke-linejoin="round"/>`;
    }
    if (s.type === 'rect') return `<rect x="${s.x}" y="${s.y}" width="${s.w}" height="${s.h}" fill="${esc(s.fill || 'none')}" stroke="${esc(s.stroke || 'none')}" stroke-width="${s.width || 0}"/>`;
    if (s.type === 'ellipse') return `<ellipse cx="${s.x}" cy="${s.y}" rx="${Math.abs(s.w / 2)}" ry="${Math.abs(s.h / 2)}" fill="${esc(s.fill || 'none')}" stroke="${esc(s.stroke || 'none')}" stroke-width="${s.width || 0}"/>`;
    if (s.type === 'line') return `<line x1="${s.x}" y1="${s.y}" x2="${s.x2}" y2="${s.y2}" stroke="${esc(s.stroke || '#111')}" stroke-width="${s.width || 2}"/>`;
    if (s.type === 'text') {
      const size = s.size || 24;
      const fill = s.fill || '#111';
      const content = s.text || '';
      const cnv = document.createElement('canvas');
      const cg = cnv.getContext('2d');
      cg.font = `${size}px sans-serif`;
      const tw = Math.max(1, Math.ceil(cg.measureText(content).width) + 2);
      const th = Math.max(1, Math.ceil(size * 1.4));
      cnv.width = tw;
      cnv.height = th;
      const g2 = cnv.getContext('2d');
      g2.font = `${size}px sans-serif`;
      g2.fillStyle = fill;
      g2.textBaseline = 'alphabetic';
      g2.fillText(content, 0, size);
      return `<g><text x="${s.x}" y="${s.y}" fill="${esc(fill)}" font-size="${size}" font-family="sans-serif" text-anchor="start" opacity="0">${escapeXml(content)}</text><image href="${cnv.toDataURL('image/png')}" x="${s.x}" y="${s.y - size}" width="${tw}" height="${th}"/></g>`;
    }
    if (s.type === 'image' && s.svg) {
      const size = svgIntrinsicSize(s.svg);
      const w = s.w || size.w;
      const h = s.h || size.h;
      const inner = svgInnerMarkup(s.svg);
      const vb = `${size.vx} ${size.vy} ${size.vw} ${size.vh}`;
      return `<svg x="${s.x || 0}" y="${s.y || 0}" width="${w}" height="${h}" viewBox="${vb}" overflow="visible">${inner}</svg>`;
    }
    return '';
}

function shapesToSvg(shapes) {
  return `<?xml version="1.0"?><svg xmlns="http://www.w3.org/2000/svg" width="480" height="360" viewBox="0 0 480 360">${(shapes || []).map(shapeToSvg).join('')}</svg>`;
}

function parseSvg(svg) {
  if (!svg) return [];
  let doc;
  try {
    doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
  } catch {
    return [];
  }
  if (doc.querySelector('parsererror')) return [];
  const shapes = [];
  const rootStyle = { fill: '#000000', stroke: 'none', width: 1 };
  const walk = (el, xf, style, into) => {
    if (!el || el.nodeType !== 1) return;
    const tag = el.tagName.toLowerCase().replace(/^.*:/, '');
    const st = inheritStyle(el, style);
    let m = mul(xf, parseTransform(el.getAttribute('transform')));
    if (tag === 'svg') m = mul(m, svgViewMatrix(el));
    if (tag === 'g' || tag === 'svg' || tag === 'defs') {
      if (tag === 'defs') return;
      if (tag === 'g' && el.getAttribute('data-editor-group') != null) {
        const kids = [];
        for (const child of el.children) walk(child, m, st, kids);
        if (kids.length === 1) into.push(kids[0]);
        else if (kids.length > 1) into.push({ type: 'group', children: kids });
        return;
      }
      for (const child of el.children) walk(child, m, st, into);
      return;
    }
    const fill = st.fill;
    const stroke = st.stroke;
    const width = st.width;
    if (tag === 'rect') {
      const p = apply(m, Number(el.getAttribute('x') || 0), Number(el.getAttribute('y') || 0));
      const w = Number(el.getAttribute('width') || 0);
      const h = Number(el.getAttribute('height') || 0);
      const q = apply(m, Number(el.getAttribute('x') || 0) + w, Number(el.getAttribute('y') || 0) + h);
      into.push({
        type: 'rect',
        x: Math.min(p.x, q.x),
        y: Math.min(p.y, q.y),
        w: Math.abs(q.x - p.x),
        h: Math.abs(q.y - p.y),
        fill, stroke, width,
      });
    } else if (tag === 'ellipse') {
      const p = apply(m, Number(el.getAttribute('cx') || 0), Number(el.getAttribute('cy') || 0));
      const rx = Number(el.getAttribute('rx') || 0);
      const ry = Number(el.getAttribute('ry') || 0);
      const r = apply(m, Number(el.getAttribute('cx') || 0) + rx, Number(el.getAttribute('cy') || 0) + ry);
      into.push({
        type: 'ellipse',
        x: p.x, y: p.y,
        w: Math.abs(r.x - p.x) * 2,
        h: Math.abs(r.y - p.y) * 2,
        fill, stroke, width,
        circle: Math.abs(rx - ry) < 1,
      });
    } else if (tag === 'circle') {
      const p = apply(m, Number(el.getAttribute('cx') || 0), Number(el.getAttribute('cy') || 0));
      const rad = Number(el.getAttribute('r') || 0);
      const r = apply(m, Number(el.getAttribute('cx') || 0) + rad, Number(el.getAttribute('cy') || 0));
      const dia = Math.abs(r.x - p.x) * 2;
      into.push({ type: 'ellipse', x: p.x, y: p.y, w: dia, h: dia, fill, stroke, width, circle: true });
    } else if (tag === 'line') {
      const a = apply(m, Number(el.getAttribute('x1') || 0), Number(el.getAttribute('y1') || 0));
      const b = apply(m, Number(el.getAttribute('x2') || 0), Number(el.getAttribute('y2') || 0));
      into.push({ type: 'line', x: a.x, y: a.y, x2: b.x, y2: b.y, stroke: stroke === 'none' ? '#111' : stroke, width, fill: 'none' });
    } else if (tag === 'path') {
      const parsed = parsePathD(el.getAttribute('d') || '');
      if (parsed.segs.length) {
        parsed.segs.forEach((sg) => {
          const a = apply(m, sg.x, sg.y);
          const hin = apply(m, sg.x + sg.ix, sg.y + sg.iy);
          const hout = apply(m, sg.x + sg.ox, sg.y + sg.oy);
          sg.x = a.x; sg.y = a.y;
          sg.ix = hin.x - a.x; sg.iy = hin.y - a.y;
          sg.ox = hout.x - a.x; sg.oy = hout.y - a.y;
        });
        into.push({
          type: 'path',
          segs: parsed.segs,
          fill,
          stroke,
          width,
          closed: parsed.closed,
        });
      }
    } else if (tag === 'polygon' || tag === 'polyline') {
      const pts = (el.getAttribute('points') || '').trim().split(/[\s,]+/).map(Number);
      const arr = [];
      for (let i = 0; i + 1 < pts.length; i += 2) arr.push(apply(m, pts[i], pts[i + 1]));
      if (arr.length) into.push({ type: 'path', segs: ptsToSegs(arr), fill, stroke, width, closed: tag === 'polygon' });
    } else if (tag === 'text') {
      const p = apply(m, Number(el.getAttribute('x') || 0), Number(el.getAttribute('y') || 0));
      const size = Number(el.getAttribute('font-size') || st.size || 24);
      into.push({ type: 'text', x: p.x, y: p.y, text: el.textContent || '', fill, size });
    } else if (tag === 'image') {
      const href = el.getAttribute('href') || el.getAttributeNS('http://www.w3.org/1999/xlink', 'href') || el.getAttribute('xlink:href') || '';
      const x = Number(el.getAttribute('x') || 0);
      const y = Number(el.getAttribute('y') || 0);
      const w = Number(el.getAttribute('width') || 0);
      const h = Number(el.getAttribute('height') || 0);
      const p = apply(m, x, y);
      const q = apply(m, x + w, y + h);
      const nw = Math.abs(q.x - p.x) || w || 1;
      const nh = Math.abs(q.y - p.y) || h || 1;
      const inner = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${nw}" height="${nh}" viewBox="0 0 ${w || nw} ${h || nh}"><image href="${href}" xlink:href="${href}" width="${w || nw}" height="${h || nh}"/></svg>`;
      into.push({ type: 'image', svg: inner, x: Math.min(p.x, q.x), y: Math.min(p.y, q.y), w: nw, h: nh });
    }
  };
  walk(doc.documentElement, [1, 0, 0, 1, 0, 0], rootStyle, shapes);
  return shapes;
}

function hitShape(s, p, ui) {
  if (!s) return false;
  if (s.type === 'group') {
    for (let i = (s.children || []).length - 1; i >= 0; i--) {
      if (hitShape(s.children[i], p, ui)) return true;
    }
    return false;
  }
  if (s.type === 'path' && s.segs?.length) {
    const on = nearestOnPath(p, s.segs, s.closed);
    if (on && on.dist <= Math.max(ui(6), (s.width || 2) + ui(4))) return true;
    if (s.closed && pathContains(p, s.segs, true)) return true;
    return false;
  }
  if (s.type === 'path' && s.pts?.some((q) => Math.hypot(q.x - p.x, q.y - p.y) < Math.max(ui(8), (s.width || 2) + ui(4)))) return true;
  if (s.type === 'line' && distToSeg(p, s) < ui(8)) return true;
  const b = bounds(s);
  if (p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h) {
    if (s.type === 'ellipse') {
      const nx = (p.x - s.x) / ((s.w || 1) / 2);
      const ny = (p.y - s.y) / ((s.h || 1) / 2);
      return nx * nx + ny * ny <= 1.15;
    }
    return true;
  }
  return false;
}

function inheritStyle(el, parent) {
  const css = cssMap(el.getAttribute('style'));
  const attr = (name) => {
    const v = el.getAttribute(name);
    return v == null || v === '' ? null : v;
  };
  const pick = (name, fallback) => css[name] || attr(name) || fallback;
  return {
    fill: pick('fill', parent.fill),
    stroke: pick('stroke', parent.stroke),
    width: Number(pick('stroke-width', parent.width)) || 0,
    size: Number(pick('font-size', parent.size || 24)) || 24,
  };
}

function cssMap(str) {
  const out = {};
  if (!str) return out;
  for (const part of str.split(';')) {
    const i = part.indexOf(':');
    if (i < 0) continue;
    out[part.slice(0, i).trim().toLowerCase()] = part.slice(i + 1).trim();
  }
  return out;
}

function svgViewMatrix(el) {
  const num = (name) => Number(String(el.getAttribute(name) || '').replace(/px$/i, '')) || 0;
  const x = num('x');
  const y = num('y');
  const w = num('width');
  const h = num('height');
  const vbRaw = (el.getAttribute('viewBox') || '').replace(/,/g, ' ').trim();
  const vb = vbRaw.split(/\s+/).map(Number);
  if (vb.length >= 4 && vb[2] > 0 && vb[3] > 0) {
    const sx = (w || vb[2]) / vb[2];
    const sy = (h || vb[3]) / vb[3];
    return mul([1, 0, 0, 1, x, y], mul([sx, 0, 0, sy, 0, 0], [1, 0, 0, 1, -vb[0], -vb[1]]));
  }
  return [1, 0, 0, 1, x, y];
}

function parseTransform(str) {
  let m = [1, 0, 0, 1, 0, 0];
  if (!str) return m;
  const re = /(translate|scale|rotate|matrix)\s*\(([^)]*)\)/g;
  let match;
  while ((match = re.exec(str))) {
    const args = match[2].split(/[\s,]+/).map(Number).filter((n) => !Number.isNaN(n));
    if (match[1] === 'translate') m = mul(m, [1, 0, 0, 1, args[0] || 0, args[1] || 0]);
    else if (match[1] === 'scale') m = mul(m, [args[0] ?? 1, 0, 0, args[1] ?? args[0] ?? 1, 0, 0]);
    else if (match[1] === 'rotate') {
      const a = ((args[0] || 0) * Math.PI) / 180;
      const cx = args[1] || 0;
      const cy = args[2] || 0;
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      m = mul(m, [1, 0, 0, 1, cx, cy]);
      m = mul(m, [cos, sin, -sin, cos, 0, 0]);
      m = mul(m, [1, 0, 0, 1, -cx, -cy]);
    } else if (match[1] === 'matrix' && args.length >= 6) m = mul(m, args);
  }
  return m;
}

function mul(a, b) {
  return [
    a[0] * b[0] + a[2] * b[1],
    a[1] * b[0] + a[3] * b[1],
    a[0] * b[2] + a[2] * b[3],
    a[1] * b[2] + a[3] * b[3],
    a[0] * b[4] + a[2] * b[5] + a[4],
    a[1] * b[4] + a[3] * b[5] + a[5],
  ];
}

function apply(m, x, y) {
  return { x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] };
}

function pathToPoints(d) {
  const pts = [];
  const tokens = d.replace(/,/g, ' ').match(/[MmLlHhVvCcQqTtSsAaZz]|-?\d*\.?\d+(?:e[-+]?\d+)?/g) || [];
  let i = 0;
  let cx = 0, cy = 0, sx = 0, sy = 0;
  const add = (x, y) => { pts.push({ x, y }); cx = x; cy = y; };
  while (i < tokens.length) {
    const cmd = tokens[i++];
    const isRel = cmd === cmd.toLowerCase();
    const take = (n) => {
      const out = [];
      for (let k = 0; k < n && i < tokens.length && !/^[A-Za-z]$/.test(tokens[i]); k++) out.push(Number(tokens[i++]));
      return out;
    };
    if (/z/i.test(cmd)) { add(sx, sy); continue; }
    if (/m/i.test(cmd)) {
      const [x, y] = take(2);
      const nx = isRel ? cx + x : x, ny = isRel ? cy + y : y;
      add(nx, ny); sx = nx; sy = ny;
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const [a, b] = take(2);
        add(isRel ? cx + a : a, isRel ? cy + b : b);
      }
    } else if (/l/i.test(cmd)) {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const [x, y] = take(2);
        add(isRel ? cx + x : x, isRel ? cy + y : y);
      }
    } else if (/h/i.test(cmd)) {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) add(isRel ? cx + Number(tokens[i++]) : Number(tokens[i++]), cy);
    } else if (/v/i.test(cmd)) {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) add(cx, isRel ? cy + Number(tokens[i++]) : Number(tokens[i++]));
    } else if (/[cqst]/i.test(cmd)) {
      const n = /c/i.test(cmd) ? 6 : /[qs]/i.test(cmd) ? 4 : 2;
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const a = take(n);
        const x = isRel ? cx + a[a.length - 2] : a[a.length - 2];
        const y = isRel ? cy + a[a.length - 1] : a[a.length - 1];
        add(x, y);
      }
    } else if (/a/i.test(cmd)) {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const a = take(7);
        add(isRel ? cx + a[5] : a[5], isRel ? cy + a[6] : a[6]);
      }
    }
  }
  return pts;
}

function escapeXml(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
function esc(s) { return escapeXml(s); }
function distToSeg(p, s) {
  const ax = s.x, ay = s.y, bx = s.x2, by = s.y2;
  const l2 = (bx - ax) ** 2 + (by - ay) ** 2 || 1;
  const t = Math.max(0, Math.min(1, ((p.x - ax) * (bx - ax) + (p.y - ay) * (by - ay)) / l2));
  return Math.hypot(p.x - (ax + t * (bx - ax)), p.y - (ay + t * (by - ay)));
}
